import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  ChevronDown, ChevronLeft, ChevronRight, Clock, MessageSquare, Search,
  User as UserIcon,
} from "lucide-react";
import { cn } from "../lib/utils";
import { API } from "@/lib/api";
import {
  dagLabel, tijdLabel, maandGroepen, maandLabel, zoekSprong,
} from "../lib/tijdmachine";
import { useTaal } from "@/contexts/TaalContext";

/**
 * De tijdmachine: terugbladeren door wat er wanneer is gezegd.
 *
 * **FRANKS EIS, 24 augustus 2026:** alle gesprekken, alleen kijken, en van een
 * bericht naar het gesprek kunnen springen.
 *
 * **ALLEEN KIJKEN, EN DAT IS EEN BOUWEIGENSCHAP EN GEEN BELOFTE.** Dit
 * onderdeel doet uitsluitend `axios.get`. Er is geen enkele post, put, patch of
 * delete, en er is geen knop die iets terugzet. Een wachter in
 * `TimeMachine.test.js` eist dat, zodat het niet stil kan veranderen.
 *
 * **WAAROM EEN DAGLIJST EN GEEN KALENDER.** Gemeten op de levende database op
 * 24 augustus 2026: 634 berichten over 16 dagen, in een periode van 31 dagen.
 * Achtenveertig procent van de dagen is leeg, dus een kalenderraster zou voor
 * bijna de helft uit lege vakjes bestaan.
 *
 * **WAAROM EEN KOLOM EN GEEN TWEE PANELEN.** Twee kolommen naast elkaar passen
 * niet op 390 px, en dat is de breedte waarop dit scherm het vaakst wordt
 * gebruikt. Een kolom met een terugstap werkt op elke breedte hetzelfde, en er
 * is dan geen tweede opmaak die apart stuk kan gaan. Dat is deze week twee keer
 * misgegaan op precies zulke schermen.
 *
 * **MAANDKOPPEN EN EEN DATUMVELD, 24 augustus 2026.** De daglijst groeit mee
 * met Franks actieve dagen. Gemeten in de echte dialoog in een echte browser,
 * op 390 en op 957 met dezelfde uitkomst: het venster is 460 px, een dagrij is
 * 48 px hoog met een stap van 52 px, dus er passen ACHT hele rijen. Bij 16
 * dagen staat er dus al meer buiten beeld dan erin. Vandaar koppen per maand
 * die inklappen, en een veld om naar een datum te springen.
 *
 * **INKLAPPEN GEBEURT NIET ALTIJD.** Past alles in het venster, dan staat alles
 * open; een kop die iets wegneemt zonder dat er iets te winnen valt, is alleen
 * maar een klik erbij. De drempel zit in `maandGroepen` en niet hier, zodat hij
 * op UITKOMST te toetsen is.
 *
 * **HET DATUMVELD ZOEKT NIET OP WOORDEN.** Dat blijft "Zoek overal", een eigen
 * punt. Dit veld doet er precies een ding: naar een tijdstip springen.
 */
export default function TimeMachine({ open, onOpenChange, onSelectConversation }) {
  const { t } = useTaal();
  const [dagen, setDagen] = useState(null);
  const [totaal, setTotaal] = useState(0);
  const [dag, setDag] = useState(null);
  const [berichten, setBerichten] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  // Wat de gebruiker zelf open- of dichtklapte. Leeg betekent: de standaard uit
  // `maandGroepen` geldt. Een lege `{}` is dus iets anders dan een keuze.
  const [keuzes, setKeuzes] = useState({});
  const [zoek, setZoek] = useState("");
  const [mededeling, setMededeling] = useState(null);
  const [springNaar, setSpringNaar] = useState(null);
  const maandRefs = useRef({});

  // Bij het sluiten alles terug naar de beginstand. Zonder dit staat de
  // tijdmachine bij het heropenen nog op de dag van de vorige keer, en dat
  // leest als een fout terwijl het een restant is.
  useEffect(() => {
    if (!open) {
      setDag(null);
      setBerichten(null);
      setFout(null);
      setKeuzes({});
      setZoek("");
      setMededeling(null);
    }
  }, [open]);

  const haalIndex = useCallback(async () => {
    setBezig(true);
    setFout(null);
    try {
      const { data } = await axios.get(`${API}/conversations/timeline`);
      setDagen(data.days || []);
      setTotaal(data.total || 0);
    } catch (e) {
      // Een fout hoort ZICHTBAAR te zijn. Een lege lijst tonen zou zeggen "er
      // is niets gezegd", en dat is iets anders dan "ik kon het niet ophalen".
      setFout("De tijdlijn kon niet worden opgehaald.");
      setDagen([]);
    } finally {
      setBezig(false);
    }
  }, []);

  useEffect(() => {
    if (open && dagen === null) haalIndex();
  }, [open, dagen, haalIndex]);

  /** Een dag openen. `viaSprong` laat de mededeling staan: die legt juist UIT
   *  waarom je op een andere dag uitkomt dan je vroeg. */
  const openDag = useCallback(async (welke, { viaSprong = false } = {}) => {
    setDag(welke);
    setBerichten(null);
    setBezig(true);
    setFout(null);
    if (!viaSprong) setMededeling(null);
    try {
      const { data } = await axios.get(`${API}/conversations/timeline/${welke}`);
      setBerichten(data.messages || []);
    } catch (e) {
      setFout(`De berichten van ${welke} konden niet worden opgehaald.`);
      setBerichten([]);
    } finally {
      setBezig(false);
    }
  }, []);

  const groepen = useMemo(() => maandGroepen(dagen, { keuzes }), [dagen, keuzes]);

  const klapMaand = useCallback((maand, nuOpen) => {
    setKeuzes((k) => ({ ...k, [maand]: !nuOpen }));
  }, []);

  /**
   * Wat er gebeurt als je op Ga drukt.
   *
   * De hele beslissing zit in `zoekSprong`, een pure functie. Hier staat alleen
   * wat er op het scherm mee gebeurt. Die scheiding is met opzet: het BESLUIT
   * is dan op uitkomst te toetsen zonder scherm, en dit deel gaat alleen nog
   * over tonen.
   */
  const springen = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    const uitkomst = zoekSprong(dagen || [], zoek);

    if (uitkomst.soort === "onleesbaar") {
      setMededeling({
        soort: "onleesbaar",
        tekst: `Ik kan "${String(zoek).trim()}" niet lezen. Probeer 2026-07, juli 2026, 2026-07-15 of 15 juli 2026.`,
      });
      return;
    }
    if (uitkomst.soort === "leeg") {
      setMededeling({ soort: "leeg", tekst: t("Er is nog geen enkele dag om heen te springen.") });
      return;
    }
    if (uitkomst.soort === "maand") {
      setDag(null);
      setBerichten(null);
      setKeuzes((k) => ({ ...k, [uitkomst.maand]: true }));
      setMededeling(null);
      setSpringNaar(uitkomst.maand);
      return;
    }

    // Een dag. Stond er niets op de gevraagde datum, dan wordt dat GEZEGD en
    // niet stil vervangen; `gevraagd` bestaat precies daarvoor.
    if (uitkomst.gevraagd) {
      const gevraagd = uitkomst.gevraagdSoort === "maand"
        ? `In ${maandLabel(uitkomst.gevraagd)}`
        : `Op ${dagLabel(uitkomst.gevraagd)}`;
      setMededeling({
        soort: "vervangen",
        tekst: `${gevraagd} is niets gezegd. De dichtstbijzijnde dag is ${dagLabel(uitkomst.datum)}.`,
      });
    } else {
      setMededeling(null);
    }
    openDag(uitkomst.datum, { viaSprong: true });
  }, [dagen, zoek, openDag, t]);

  // Naar een maandkop scrollen NADAT hij is getekend. Dit staat in een effect
  // en niet in de knop, want op het moment van klikken bestaat de opengeklapte
  // maand nog niet.
  useEffect(() => {
    if (!springNaar) return;
    const el = maandRefs.current[springNaar];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "start" });
    }
    setSpringNaar(null);
  }, [springNaar, groepen]);

  const heeftDagen = Array.isArray(dagen) && dagen.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] p-0 gap-0" data-testid="time-machine">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="font-serif text-lg flex items-center gap-2 min-w-0">
            {dag ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="tap-target h-7 w-7 -ml-1 shrink-0"
                  onClick={() => { setDag(null); setBerichten(null); setFout(null); setMededeling(null); }}
                  aria-label={t("Terug naar de dagen")}
                  data-testid="time-machine-terug"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="truncate">{dagLabel(dag)}</span>
              </>
            ) : (
              <>
                <Clock className="h-4 w-4 shrink-0" />
                <span className="truncate">{t("Tijdmachine")}</span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Het datumveld staat BUITEN de ScrollArea, zodat het blijft staan
            terwijl je door de lijst schuift. Het verschijnt alleen wanneer er
            iets te springen valt: een veld boven een lege tijdlijn is een
            belofte die nergens heen gaat. */}
        {heeftDagen ? (
          <form
            onSubmit={springen}
            className="flex items-center gap-2 px-4 pb-3 pt-1"
            data-testid="time-machine-springform"
          >
            <label htmlFor="tm-datum" className="sr-only">{t("Spring naar een datum of maand")}</label>
            <Input
              id="tm-datum"
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              placeholder={t("2026-07, juli 2026 of 15 juli 2026")}
              className="h-8 text-sm min-w-0"
              data-testid="time-machine-datumveld"
              autoComplete="off"
            />
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              className="tap-target h-8 shrink-0"
              data-testid="time-machine-ga"
            >
              <Search className="h-3.5 w-3.5 mr-1" />
              {t("Ga")}
            </Button>
          </form>
        ) : null}

        {mededeling ? (
          <p
            className="mx-4 mb-3 rounded-md bg-[hsl(var(--muted))] px-3 py-2 text-sm break-words"
            data-testid="time-machine-mededeling"
            role="status"
          >
            {mededeling.tekst}
          </p>
        ) : null}

        {/* `[&>div>div]:!block` is verplicht op ELKE ScrollArea in deze app.
            Radix rendert het kind van zijn viewport als `display: table` met
            `min-width: 100%`, en een tabel krimpt naar zijn INHOUD in plaats van
            zijn container te gehoorzamen; op een telefoon loopt de inhoud dan
            buiten beeld en doet `truncate` niets. Deze reparatie was op 24
            augustus 2026 al drie keer nodig (Sidebar 26 juli, ChatArea en
            SearchDialog 24 augustus). `npm run meet:klassen` telt per voorkomen
            dat elke ScrollArea hem draagt, dus een vierde kan er niet stil bij. */}
        <ScrollArea className="max-h-[460px] [&>div>div]:!block">
          <div className="px-2 pb-4 min-w-0">
            {fout ? (
              <div className="text-center py-8 text-muted-foreground px-4" data-testid="time-machine-fout">
                {fout}
              </div>
            ) : bezig ? (
              <div className="text-center py-8 text-muted-foreground">{t("Bezig...")}</div>
            ) : dag ? (
              <DagWeergave
                berichten={berichten}
                onSelectConversation={onSelectConversation}
              />
            ) : (
              <MaandenLijst
                groepen={groepen}
                dagen={dagen}
                totaal={totaal}
                onOpen={openDag}
                onKlap={klapMaand}
                maandRefs={maandRefs}
              />
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

/** De maanden, nieuwste boven, met daaronder de dagen die iets hebben. */
function MaandenLijst({ groepen, dagen, totaal, onOpen, onKlap, maandRefs }) {
  const { t } = useTaal();
  if (!dagen || dagen.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground" data-testid="time-machine-leeg">
        <p>{t("Er is nog niets om terug te kijken.")}</p>
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid="time-machine-dagen">
      <p className="px-3 py-2 text-xs text-muted-foreground">
        {t("{berichten} over {dagen}", {
          berichten: totaal === 1 ? t("1 bericht") : t("{n} berichten", { n: totaal }),
          dagen: dagen.length === 1 ? t("1 dag") : t("{n} dagen", { n: dagen.length }) })}
      </p>
      {groepen.map((groep) => (
        <div key={groep.maand} data-testid={`time-machine-maand-${groep.maand}`}>
          <button
            type="button"
            onClick={() => onKlap(groep.maand, groep.open)}
            aria-expanded={groep.open}
            aria-controls={`tm-dagen-${groep.maand}`}
            ref={(el) => { maandRefs.current[groep.maand] = el; }}
            className={cn(
              "w-full text-left px-3 py-2 rounded-lg min-w-0",
              "hover:bg-[hsl(var(--muted))] transition-colors",
              "focus:outline-none focus:bg-[hsl(var(--muted))]"
            )}
            data-testid={`time-machine-maandkop-${groep.maand}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {groep.open ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <span className="flex-1 min-w-0 truncate font-medium">{groep.label}</span>
              <span className="text-xs text-muted-foreground flex-shrink-0 tabular-nums">
                {groep.aantalDagen === 1 ? t("1 dag") : t("{n} dagen", { n: groep.aantalDagen })}, {groep.aantalBerichten === 1 ? t("1 bericht") : t("{n} berichten", { n: groep.aantalBerichten })}
              </span>
            </div>
          </button>

          {groep.open ? (
            <div
              id={`tm-dagen-${groep.maand}`}
              className="space-y-1"
              data-testid={`time-machine-maand-dagen-${groep.maand}`}
            >
              {groep.dagen.map((d) => (
                <button
                  key={d.date}
                  onClick={() => onOpen(d.date)}
                  className={cn(
                    "w-full text-left px-3 py-3 rounded-lg min-w-0",
                    "hover:bg-[hsl(var(--muted))] transition-colors",
                    "focus:outline-none focus:bg-[hsl(var(--muted))]"
                  )}
                  data-testid={`time-machine-dag-${d.date}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="flex-1 min-w-0 truncate font-medium">{dagLabel(d.date)}</span>
                    <span className="text-xs text-muted-foreground flex-shrink-0">{d.count}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Wat er op een dag is gezegd, op tijd, met de weg terug. */
function DagWeergave({ berichten, onSelectConversation }) {
  const { t } = useTaal();
  if (!berichten || berichten.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground" data-testid="time-machine-dag-leeg">
        <p>{t("Op deze dag is niets gezegd.")}</p>
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid="time-machine-berichten">
      {berichten.map((m) => (
        <button
          key={m.id}
          onClick={() => onSelectConversation(m.conversation_id)}
          className={cn(
            "w-full text-left px-3 py-3 rounded-lg min-w-0",
            "hover:bg-[hsl(var(--muted))] transition-colors",
            "focus:outline-none focus:bg-[hsl(var(--muted))]"
          )}
          data-testid={`time-machine-bericht-${m.id}`}
        >
          <div className="flex items-start gap-3 min-w-0">
            {m.role === "user" ? (
              <UserIcon className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
            ) : (
              <MessageSquare className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="text-xs text-muted-foreground flex-shrink-0 tabular-nums">
                  {tijdLabel(m.timestamp)}
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  {m.conversation_title}
                </span>
              </div>
              {/* `break-words` en niet `truncate`: een fragment mag over twee
                  regels, maar nooit buiten de doos. Zonder dit loopt een lang
                  woord op een telefoon het scherm uit. */}
              <p className="text-sm mt-1 line-clamp-2 break-words">
                {m.snippet}
                {m.truncated ? <span className="text-muted-foreground">...</span> : null}
              </p>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
